import React, { useCallback, useEffect, useState, useContext } from 'react';
import classnames from 'classnames';
import { Link , graphql, useStaticQuery } from 'gatsby';
import { defineMessages, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import Menu from 'components/menu/menu';
import ExternalProducts from 'components/menu/externalProducts';
import MobileNav from 'components/menu/mobileNav';
import icon from 'components/image/headerIcon';
import { Language, PageType } from 'enums/types';
import { StateContext } from 'globalState';
import { routes } from 'languages';
import useWindowWidth from 'hooks/useWindowWidth';
import { ibLogin, ibSignUp } from 'helpers/ibLinks';
import { isSafari } from '../../browserHelper';
import LanguageSwitcher from '../languageSwitcher/languageSwitcher';
import * as style from './header.module.scss';

interface Props {
	showLanguageSwitcher?: boolean;
	originalPath: string;
	pageType: PageType;
}

export interface NavigationLink {
	title: MessageDescriptor;
	link?: string;
	externalLink?: string;
}

export interface NavigationSection {
	title: MessageDescriptor;
	links: NavigationLink[];
}

const translations = defineMessages({
	accountTitle: {
		id: 'navigation.account',
		defaultMessage: 'Account',
	},
	personalTitle: {
		id: 'navigation.personal',
		defaultMessage: 'Personal',
	},
	businessTitle: {
		id: 'navigation.business',
		defaultMessage: 'Business',
	},
	loansTitle: {
		id: 'title.loans',
		defaultMessage: 'Loans',
	},

	aboutTitle: {
		id: 'navigation.about',
		defaultMessage: 'About',
	},
	teamTitle: {
		id: 'navigation.team',
		defaultMessage: 'Team',
	},
	pricingTitle: {
		id: 'navigation.pricing',
		defaultMessage: 'Pricing',
	},
	joinTitle: {
		id: 'navigation.joinUs',
		defaultMessage: 'Join us',
	},
	contactTitle: {
		id: 'navigation.contact',
		defaultMessage: 'Contact',
	},
	spectrocoinText: {
		id: 'nav.spectrocoin.text',
		defaultMessage: 'Cryptocurrency wallet and exchange',
	},
	loansText: {
		id: 'nav.loans.text',
		defaultMessage: 'Cryptocurrency-backed loans',
	},
	exchangeText: {
		id: 'nav.exchange.text',
		defaultMessage: 'Crypto assets exchange',
	},
	faqTitle: {
		id: 'nav.faq',
		defaultMessage: 'FAQ',
	},
	blogTitle: {
		id: 'nav.blog',
		defaultMessage: 'Blog',
	},
	trustCenterTitle: {
		id: 'nav.trustCenter',
		defaultMessage: 'Trust Center',
	},
	logoImageDescription: {
		id: 'linkDescription.logo',
		defaultMessage: 'Bankera logo leads you to homepage',
	},
	personalCardTitle: {
		id: 'navigation.card',
		defaultMessage: 'Card',
	},
	personalCardPricingTitle: {
		id: 'navigation.cardPricing',
		defaultMessage: 'Card pricing',
	},
	businessCardTitle: {
		id: 'navigation.card',
		defaultMessage: 'Card',
	},
	fintechTitle: {
		id: 'navigation.fintech',
		defaultMessage: 'Fintech',
	},
	whiteLabelTitle: {
		id: 'navigation.whiteLabel',
		defaultMessage: 'White-label',
	},
	cardIssuingTitle: {
		id: 'navigation.cardIssuing',
		defaultMessage: 'Card issuing',
	},
	paymentGatewayTitle: {
		id: 'navigation.paymentGateway',
		defaultMessage: 'Payment gateway',
	},
	apiTitle: {
		id: 'navigation.api',
		defaultMessage: 'API',
	},
	baasTitle: {
		id: 'navigation.baas',
		defaultMessage: 'Banking-as-a-Service',
	},
	ibansTitle: {
		id: 'navigation.ibans',
		defaultMessage: 'Virtual IBANs',
	},
	corporateSolutionsTitle: {
		id: 'navigation.corporateSolutions',
		defaultMessage: 'Solutions',
	},
	binSponsorshipTitle: {
		id: 'navigation.binSponsorship',
		defaultMessage: 'BIN sponsorship',
	},
});

enum ScreenType {
	Desktop = 'desktop',
	Tablet = 'tablet',
	Mobile = 'mobile',
}

const Header = ({ showLanguageSwitcher, originalPath, pageType }: Props) => {
	const [menuType, setMenuType] = useState(ScreenType.Mobile);
	const { formatMessage, locale } = useIntl();
	const { store } = useContext(StateContext);
	const { isLoginDisabled } = store;
	useWindowWidth();

	const getScreenType = useCallback(() => {
		if (typeof window === 'undefined') {
			return ScreenType.Mobile;
		}

		let isMobileSize = window.innerWidth < 840;
		let isTabletSize = window.innerWidth > 799 && window.innerWidth < 1120;

		if (locale === Language.Polish) {
			isMobileSize = window.innerWidth < 870;
			isTabletSize = window.innerWidth > 869 && window.innerWidth < 1120;
		}

		if (isMobileSize) return ScreenType.Mobile;
		if (isTabletSize) return ScreenType.Tablet;
		if (window.innerWidth > 1119) return ScreenType.Desktop;

		return ScreenType.Mobile;
	}, [locale]);

	const updateSize = useCallback(() => {
		const screenType = getScreenType();
		setMenuType((prevMenuType) => (screenType === prevMenuType ? prevMenuType : screenType));
	}, [getScreenType]);

	useEffect(() => {
		updateSize();
		if (typeof window === 'undefined') {
			return undefined;
		}

		window.addEventListener('resize', updateSize);
		return () => {
			window.removeEventListener('resize', updateSize);
		};
	}, [updateSize]);

	const nav: NavigationSection[] = [
		{
			title: translations.personalTitle,
			links: [
				{ title: translations.accountTitle, link: routes[locale]['/'] },
				{ title: translations.personalCardTitle, link: routes[locale]['/personal/card/'] },
				{ title: translations.pricingTitle, link: routes[locale]['/pricing/personal-account/'] },
			],
		},
		{
			title: translations.businessTitle,
			links: [
				{ title: translations.accountTitle, link: routes[locale]['/business/'] },
				{ title: translations.businessCardTitle, link: routes[locale]['/business/card/'] },
				{ title: translations.paymentGatewayTitle, link: routes[locale]['/payment-gateway/'] },
				...([Language.Default, Language.Lithuanian, Language.Latvian].includes(locale as Language)) ? [{
					title: translations.loansTitle,
					link: routes[locale]['/business/loans/'],
				}] : [],
				{ title: translations.corporateSolutionsTitle, link: routes[locale]['/business/corporate-solutions/'] },
				{ title: translations.pricingTitle, link: routes[locale]['/pricing/business-account/'] },
			],
		},
		{
			title: translations.fintechTitle,
			links: [
				{ title: translations.whiteLabelTitle, link: routes[locale]['/fintech/white-label-banking/'] },
				{ title: translations.baasTitle, link: routes[locale]['/fintech/banking-as-a-service/'] },
				{ title: translations.cardIssuingTitle, link: routes[locale]['/fintech/card-issuing/'] },
				{ title: translations.binSponsorshipTitle, link: routes[locale]['/fintech/bin-sponsorship/'] },
				{ title: translations.ibansTitle, link: routes[locale]['/fintech/virtual-ibans/'] },
				{ title: translations.apiTitle, link: routes[locale]['/api/'] },
			],
		},
		{
			title: translations.aboutTitle,
			links: [
				{ title: translations.teamTitle, link: routes[locale]['/team/'] },
				{ title: translations.joinTitle, externalLink: 'https://careers.bankera.com' },
				{ title: translations.blogTitle, externalLink: 'https://blog.bankera.com/' },
				{ title: translations.trustCenterTitle, link: routes[locale]['/trust-center/'] },
				{ title: translations.faqTitle, link: routes[locale]['/faq/'] },
			],
		},
	];

	const img = useStaticQuery(
		graphql`{
			spectroIcon: file(relativePath: {eq: "icons/spectro-icon.png"}) {
				childImageSharp {
					gatsbyImageData(width: 80, quality: 80, placeholder: NONE, layout: CONSTRAINED)
				}
			}
			exchangeIcon: file(relativePath: {eq: "icons/exchange-icon.png"}) {
				childImageSharp {
					gatsbyImageData(width: 80, quality: 80, placeholder: NONE, layout: CONSTRAINED)
				}
			}
			loansIcon: file(relativePath: {eq: "icons/bankera-loans-icon.png"}) {
				childImageSharp {
					gatsbyImageData(width: 80, quality: 80, placeholder: NONE, layout: CONSTRAINED)
				}
			}
		}`
	);

	const externalProducts = [
		{
			icon: img.spectroIcon.childImageSharp.gatsbyImageData,
			title: 'SpectroCoin',
			subtitle: formatMessage(translations.spectrocoinText),
			link: 'https://spectrocoin.com/',
		}
	];

	return (
		<header>
			<div className={classnames(style.header, { [style.polishHeader]: locale === Language.Polish })}>
				<div className={classnames(style.logoWrapper, { [style.reverseWrapper]: locale === Language.Arabic })}>
					<Link
						to={routes[locale]['/']}
						className={classnames(style.logo, { [style.polishHeaderMargin]: locale === Language.Polish })}
						title={formatMessage(translations.logoImageDescription)}
					>
						{icon.logo}
					</Link>
					{menuType === ScreenType.Tablet && showLanguageSwitcher && (
						<LanguageSwitcher popover={true} originalPath={originalPath} pagePathId={originalPath} />
					)}
				</div>

				<div className={classnames(style.navigationSection, menuType === ScreenType.Mobile && style.hide)}>
					<Menu navigation={nav} />
					<a
						href={ibLogin}
						className={classnames(style.button, {
							[style.disabledButton]: isLoginDisabled,
							[style.safariButton]: isSafari,
						})}
					>
						<FormattedMessage id="button.login" defaultMessage="Log in" />
					</a>
					<a
						href={ibSignUp}
						className={classnames(style.filledButton, style.button, {
							[style.disabledButton]: isLoginDisabled,
							[style.arabicAlign]: locale === Language.Arabic,
							[style.safariButton]: isSafari,
						})}
					>
						<FormattedMessage id="button.signUp" defaultMessage="Sign up" />
					</a>
					<ExternalProducts externalProducts={externalProducts} />
					{menuType === ScreenType.Desktop && showLanguageSwitcher && (
						<LanguageSwitcher popover={true} originalPath={originalPath} pageType={pageType} />
					)}
				</div>

				{menuType === ScreenType.Mobile && (
					<MobileNav
						navigation={nav}
						externalProducts={externalProducts}
						originalPath={originalPath}
						showLanguageSwitcher
						pageType={pageType}
					/>
				)}
			</div>
		</header>
	);
};

export default Header;
